<template>
  <section class="accordion-section">
    <button
      class="accordion-header"
      :class="{
        'accordion-header--open': isOpen,
        'accordion-header--disabled': !isEditable
      }"
      @click="isEditable ? (isOpen = !isOpen) : null"
    >
      <div class="accordion-header__icon">
        <svg v-if="verified" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="12" fill="#10B981"/>
          <path d="M7 12.5L10.5 16L17 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <img :src="icon" v-else class="accordion-header__img" />
      </div>
      <div class="accordion-header__content">
        <h2 class="accordion-header__title">{{ info.title }}</h2>
        <p class="accordion-header__description" v-if="info.description">
          {{ info.description }}
        </p>
        <div class="accordion-header__status" v-if="status">
          <span :class="'accordion-status accordion-status--' + status.toLowerCase()">
            {{ status === 'Pending' ? 'Pending Approval' : status }}
          </span>
        </div>
      </div>
      <button
        class="accordion-link-btn"
        v-if="showButton"
        @click.stop="$emit('button-click')"
      >
        {{ verified ? "Change" : "Link" }}
      </button>
      <div class="accordion-chevron" v-else-if="!verified">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          :class="{ 'accordion-chevron--open': isOpen }"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
    </button>
    <transition
      name="accordion"
      @enter="start"
      @after-enter="end"
      @before-leave="start"
      @after-leave="end"
    >
      <div v-show="isOpen" class="accordion-body">
        <slot></slot>
      </div>
    </transition>
  </section>
</template>

<script>
  export default {
    name: "Accordion",
    data() {
      return {
        isOpen: false,
      };
    },
    inject: ["activeTab"],
    emits: ["button-click", "is-active"],
    props: {
      info: {
        type: Object,
        required: true,
      },
      verified: {
        type: Boolean,
        required: true,
      },
      showButton: {
        type: Boolean,
        required: false,
      },
      icon: {
        type: String,
        required: false,
        default: () => {
          return "https://res.cloudinary.com/djalafcj9/image/upload/v1653916136/getequityV2/check-mark-svgrepo-com_1_iqypwa.svg";
        },
      },
      //determines the state of the checked image
      status: {
        type: String,
        required: false,
      },
    },
    watch: {
      isOpen(val) {
        if (val) {
          // emit event to close other accordions
          this.$emit("is-active", this.info.title);
        }
      },
      activeTab() {
        this.checkActiveTab();
      },
    },
    computed: {
      isEditable() {
        return !(this.status === "Approved" || this.status === "Pending");
      },
    },
    mounted() {
      this.checkActiveTab();
    },
    methods: {
      start(el) {
        el.style.height = el.scrollHeight + "px";
      },
      end(el) {
        el.style.height = "";
      },
      checkActiveTab() {
        // listen for a new open accordion and close if the current title doesn't match emitted title
        if (
          this.activeTab === this.info.title &&
          this.info.title !== "Bank Account (Optional)"
        ) {
          this.isOpen = true;
        } else {
          this.isOpen = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;
$btn-primary: #001240;

.accordion-section {
  background: $bg-card;
  border-radius: 10px;
  border: 1px solid $border-color;
  margin-bottom: 16px;
  overflow: hidden;
}

.accordion-header {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s ease;

  &:hover {
    background: $bg-subtle;
  }

  &--open {
    border-bottom: 1px solid $border-color;
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.7;

    &:hover {
      background: transparent;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
  }

  &__img {
    width: 24px;
    height: 24px;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 15px;
    font-weight: 500;
    color: $text-primary;
    margin: 0 0 4px 0;
  }

  &__description {
    font-size: 13px;
    color: $text-muted;
    margin: 0;
    line-height: 1.5;
  }

  &__status {
    margin-top: 8px;
  }
}

.accordion-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 500;

  &--approved {
    background: rgba(16, 185, 129, 0.1);
    color: #10B981;
  }

  &--pending {
    background: rgba(217, 119, 6, 0.1);
    color: #D97706;
  }

  &--rejected {
    background: rgba(220, 38, 38, 0.1);
    color: #DC2626;
  }
}

.accordion-link-btn {
  flex-shrink: 0;
  padding: 8px 16px;
  background: $btn-primary;
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #06206B;
  }
}

.accordion-chevron {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $text-muted;

  svg {
    transition: transform 0.2s ease;
  }

  &--open {
    transform: rotate(180deg);
  }
}

.accordion-body {
  padding: 24px;
  max-width: 730px;
  margin: 0 auto;
}

.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition: height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  height: 0 !important;
  opacity: 0;
}
</style>
